import {
  autoPlacement,
  autoUpdate,
  type ClientRectObject,
  offset,
  type Placement,
  type Strategy,
  shift,
  useFloating,
} from "@floating-ui/react";
import { useEventCallback } from "@stenajs-webui/core";
import {
  type CSSProperties,
  type ReactNode,
  type Ref,
  type RefObject,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import type { Location, To } from "react-router-dom";
import type { Store } from "redux";

import type { ModalLocation } from "../../common/routes/AppRoutes";
import type { StoreState } from "../../store/storeState";
import { Overlay } from "./Overlay";
import type { SpotlightStyle } from "./Spotlight";
import { getNodeFromTarget } from "./util/utils";

export interface LifecycleEffectsParams {
  location: Location;
  replace: (to: To | ModalLocation) => void;
  store: Store<StoreState>;
}

export interface StepWithLifecycleEffects {
  content: string;
  disableScroll?: boolean;
  offset?: number;
  onEnter?: (params: LifecycleEffectsParams) => Promise<void>;
  onExit?: (params: LifecycleEffectsParams) => Promise<void>;
  placement?: Placement;
  spotlightStyle?: SpotlightStyle;
  target: string | string[];
  title: string;
}

export interface RenderFooterProps {
  index: number;
  isFirstStep: boolean;
  isLastStep: boolean;
  nextRef: Ref<HTMLButtonElement>;
  onNext: () => void;
  onPrevious: () => void;
  onSkip: () => void;
  previousRef: Ref<HTMLButtonElement>;
  size: number;
}

export interface TourDefinition<
  TStep extends StepWithLifecycleEffects = StepWithLifecycleEffects,
> {
  steps: TStep[];
}

export interface RenderStepProps<
  TStep extends StepWithLifecycleEffects = StepWithLifecycleEffects,
> {
  floating: (node: HTMLElement | null) => void;
  footerProps: RenderFooterProps;
  position: Strategy;
  step: TStep;
  x: number;
  y: number;
  zIndex: number;
}

interface TourProps<
  TStep extends StepWithLifecycleEffects = StepWithLifecycleEffects,
> {
  lifecycleEffectsRef: RefObject<LifecycleEffectsParams>;
  onError?: () => void;
  onFinished?: (index: number, skipped: boolean) => void;
  overlayStyle?: CSSProperties;
  renderStep: (props: RenderStepProps<TStep>) => ReactNode;
  steps: TStep[];
  zIndex?: number;
}

export function Tour<
  TStep extends StepWithLifecycleEffects = StepWithLifecycleEffects,
>({
  steps,
  onFinished,
  zIndex = 10000,
  overlayStyle,
  renderStep,
  lifecycleEffectsRef,
  ...props
}: TourProps<TStep>) {
  const [index, setIndex] = useState(0);
  const [visibleIndex, setVisibleIndex] = useState(index);
  const [rect, setRect] = useState<ClientRectObject>();
  const [step, setStep] = useState<TStep>(steps[0]);
  const lastStepDirection = useRef<"next" | "prev">("next");
  const nextRef = useRef<HTMLButtonElement>(null);
  const previousRef = useRef<HTMLButtonElement>(null);
  const autoUpdateRef = useRef<() => void | null>(null);

  const onError = useEventCallback(() => props.onError?.());

  const {
    x,
    y,
    strategy,
    elements: { floating },
    refs: { reference: targetReference, setReference, setFloating },
    update,
  } = useFloating({
    placement: step.placement ?? "right",
    strategy: "fixed",
    middleware: [
      autoPlacement(),
      offset(step.offset ?? 20),
      shift({ padding: 20 }),
    ],
  });

  useEffect(() => {
    const lastDir = lastStepDirection.current;

    function returnFocusToElement() {
      const backElement = previousRef.current;
      const nextElement = nextRef.current;

      const focusElement =
        lastDir === "prev" && backElement != null ? backElement : nextElement;

      focusElement?.focus?.();
    }

    async function changeTarget() {
      let nextStep = steps[index];

      // If no next step, go to first step
      if (!nextStep) {
        nextStep = steps[0];
        setIndex(0);
        return;
      }

      // Run onEnter lifecycle effects used to wait for animations and such to finish
      await nextStep.onEnter?.(lifecycleEffectsRef.current);
      const node = await getNodeFromTarget(nextStep.target);

      if (!node) {
        console.warn("Step target not found. Going to next step");
        if (index < steps.length - 1) {
          setIndex((i) => i + 1);
        } else {
          onError?.();
          console.warn("Next step not available. Exiting tour");
        }

        return;
      }

      // Set the new step and position the floating element
      setTimeout(() => {
        if (!nextStep.disableScroll) {
          node.scrollIntoView({ block: "center" });
        }

        setReference(node);
        setStep(nextStep);
        setVisibleIndex(index);

        // Enable auto update of the floating element position and spotlight rectangle
        if (floating && targetReference.current) {
          autoUpdateRef.current = autoUpdate(
            targetReference.current,
            floating,
            () => {
              update();
              setRect(targetReference.current?.getBoundingClientRect());
            },
          );
        }

        returnFocusToElement();
        setRect(node.getBoundingClientRect());
      }, 0);
    }

    changeTarget();
  }, [
    previousRef,
    nextRef,
    lastStepDirection,
    index,
    steps,
    lifecycleEffectsRef,
    setReference,
    onError,
    floating,
    update,
    targetReference,
  ]);

  // Spotlight rectangle position is now updated via Floating UI's autoUpdate

  useLayoutEffect(() => {
    function stopPointerDownEvents(e: MouseEvent) {
      e.stopPropagation();
    }

    window.addEventListener("pointerdown", stopPointerDownEvents, {
      capture: true,
    });

    return () => {
      window.removeEventListener("pointerdown", stopPointerDownEvents, {
        capture: true,
      });
    };
  }, []);

  const size = steps.length;
  const isLastStep = index === size - 1;
  const isFirstStep = index === 0;

  async function onExit() {
    // Remove the auto update of the floating element position
    autoUpdateRef.current?.();
    // Run onExit lifecycle effects used to wait for animations and such to finish
    await step.onExit?.(lifecycleEffectsRef.current);
  }

  const onNext = async () => {
    lastStepDirection.current = "next";

    await onExit();

    if (!isLastStep) {
      setIndex((i) => i + 1);
    } else {
      onFinished?.(index, false);
    }
  };

  const onPrevious = async () => {
    lastStepDirection.current = "prev";

    await onExit();

    if (!isFirstStep) {
      setIndex((i) => i - 1);
    }
  };

  const onSkip = async () => {
    await onExit();

    onFinished?.(index, true);
  };

  if (!targetReference.current) {
    return null;
  }

  return (
    <>
      <Overlay
        overlayStyle={overlayStyle}
        zIndex={zIndex}
        rect={rect}
        onClick={onNext}
        spotlightStyle={step.spotlightStyle}
      />
      {renderStep({
        step,
        position: strategy,
        x: x ?? 0,
        y: y ?? 0,
        zIndex,
        floating: setFloating,
        footerProps: {
          index: visibleIndex,
          size,
          isFirstStep,
          isLastStep,
          onNext,
          nextRef,
          onPrevious,
          previousRef,
          onSkip,
        },
      })}
    </>
  );
}
